/**
 * Bridge for `USER_DATA` messages posted by the native app host into the
 * webview. The native side may post before React has mounted, so messages
 * that arrive with no active subscriber are buffered and flushed to the first
 * subscriber. Listeners are reference-counted: they are attached on the first
 * subscription and detached once the last subscriber unsubscribes, so nothing
 * leaks across remounts or HMR.
 */
export type UserDataMessage = {
  type: string;
  prefillData?: Record<string, string>;
  // Capabilities of the host app. Absent on builds that predate a given feature.
  features?: { chat?: boolean };
};

type Listener = (message: UserDataMessage) => void;

const subscribers = new Set<Listener>();
const buffered: UserDataMessage[] = [];
let listening = false;
// Once the first legitimate USER_DATA payload has been consumed and the auth
// flow has started, the bridge is sealed: the native host posts user data once
// at startup, so any later message is unexpected and must not be honoured
let sealed = false;

function handleEvent(event: Event) {
  if (sealed) return;
  try {
    const raw = (event as MessageEvent).data;
    const message: UserDataMessage =
      typeof raw === "string" ? JSON.parse(raw) : raw;
    if (message?.type !== "USER_DATA") return;

    if (subscribers.size === 0) {
      buffered.push(message);
      return;
    }
    subscribers.forEach((listener) => listener(message));
  } catch {
    // Ignore malformed messages.
  }
}

function startListening() {
  if (listening || sealed) return;
  listening = true;
  // Both targets are needed: iOS WKWebView dispatches on `window`, while some
  // Android WebViews dispatch on `document`.
  window.addEventListener("message", handleEvent);
  document.addEventListener("message", handleEvent as EventListener);
}

function stopListening() {
  if (!listening) return;
  listening = false;
  window.removeEventListener("message", handleEvent);
  document.removeEventListener("message", handleEvent as EventListener);
}

export function subscribeToUserData(listener: Listener): () => void {
  startListening();
  subscribers.add(listener);

  // Flush any messages that arrived before this subscriber mounted.
  if (buffered.length > 0) {
    buffered.splice(0).forEach((message) => listener(message));
  }

  return () => {
    subscribers.delete(listener);
    if (subscribers.size === 0) stopListening();
  };
}

// Permanently close the bridge once the auth flow has started. Drops any
// buffered messages, stops delivering to subscribers, and detaches the DOM listeners
export function sealUserData(): void {
  if (sealed) return;
  sealed = true;
  buffered.length = 0;
  stopListening();
}

// Start buffering as soon as this module is loaded (i.e. when the app-webview
// route chunk loads), so a USER_DATA message posted by the native host before
// React mounts is captured and flushed to the first subscriber.
startListening();
